import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/lnlApi";
import { toast } from "sonner";
import { useAuth, ROLE_LABEL, CAN_MANAGE_USERS } from "@/lib/lnlAuth";
import Logo from "@/components/layout/Logo";
import {
    LayoutDashboard, FileText, Image as ImageIcon, Menu as MenuIcon, Home, Newspaper, BookOpen,
    HelpCircle, Sparkles, Users2, Building2, Phone, MessageSquare, ClipboardList, Wallet,
    CalendarCheck, Globe2, ListChecks, Shield, History, Search as SearchIcon, Settings as SettingsIcon,
    BadgeCheck, Bell, ChevronDown, ChevronRight, LogOut, KeyRound, ExternalLink, X, ArrowRight,
    Pencil, GraduationCap, TrendingUp, CheckCircle2, UserPlus, Trash2, Save, Database, PanelLeft,
    Eye, EyeOff,
} from "lucide-react";
import { PageHero } from "@/components/common/SectionHeading";
import { STATUS_TONE } from "@/lib/lnlConstants";
import {
    SettingsPanel, MediaPanel, NewsPanel, FaqsPanel, FacilitiesPanel, LeadershipPanel,
    StatementsPanel, ProgramsPanel, RequirementsPanel, ExportPanel, ActivityPanel, LoginActivityPanel,
    HeroSlidesPanel,
} from "./admin/CmsPanels";
import ThemeStudio from "./admin/ThemeStudio";
import CampusSubmissionsPanel from "./admin/CampusSubmissionsPanel";
import TeamChatPanel from "./admin/TeamChatPanel";
import AutoResponsesPanel from "./admin/AutoResponsesPanel";
import RecordDrawer from "./admin/RecordDrawer";

const APP_STATUSES = ["Draft","Submitted","Documents Pending","For Initial Review","Needs Revision","For Assessment","Qualified","Accepted","Waitlisted","Enrolled","Closed","Withdrawn"];

function formatErr(e) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
    return d?.msg || e?.message || "Something went wrong.";
}

// Reusable password input with show/hide toggle.
// Preserves autoComplete and required props; visible mode uses type=text.
function PasswordInput({ value, onChange, testid, autoComplete = "current-password", minLength, required, className = "" }) {
    const [show, setShow] = useState(false);
    return (
        <div className={`relative ${className}`}>
            <input
                type={show ? "text" : "password"}
                value={value}
                onChange={onChange}
                required={required}
                minLength={minLength}
                autoComplete={autoComplete}
                data-testid={testid}
                className="mt-1 w-full rounded-md border border-lnl-border pl-3 pr-10 py-2.5"
            />
            <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                aria-pressed={show}
                data-testid={testid ? `${testid}-toggle` : "password-toggle"}
                className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 p-1.5 rounded-md text-lnl-gray hover:text-lnl-charcoal hover:bg-lnl-off-white focus:outline-none focus:ring-2 focus:ring-lnl-green/40"
            >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

// ============================================================================
// LOGIN CARD (unchanged UX)
// ============================================================================
function LoginCard() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    async function onSubmit(e) {
        e.preventDefault(); setBusy(true); setError("");
        try { await login(email.trim(), password); toast.success("Signed in"); }
        catch (err) {
            const status = err?.response?.status;
            const msg = status === 429 ? "Too many failed attempts. Please wait 10 minutes, then try again."
                : status === 401 ? "Incorrect email or password. Check for typos (e.g. gmail.com) and try again."
                : (!err?.response ? "Can't reach the server. Check your connection and try again." : (formatErr(err) || "Sign-in failed"));
            setError(msg); toast.error(msg);
        }
        finally { setBusy(false); }
    }
    return (
        <div data-testid="admin-login">
            <PageHero eyebrow="Staff" title="Sign in to LNL Admin" description="Use your staff email and password. Contact a Super Administrator if you need an account." breadcrumbs={[{ label: "Home", to: "/" }, { label: "Admin" }]} />
            <section className="py-10 bg-white">
                <div className="lnl-container max-w-md">
                    <form onSubmit={onSubmit} className="lnl-card p-6">
                        <Shield className="h-6 w-6 text-lnl-green" />
                        <div className="font-heading text-xl font-bold text-lnl-charcoal mt-2">Staff sign-in</div>
                        <label className="text-xs text-lnl-gray font-heading font-semibold mt-4 block">Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" required data-testid="admin-email" autoComplete="email" />
                        <label className="text-xs text-lnl-gray font-heading font-semibold mt-3 block">Password</label>
                        <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" testid="admin-password" />
                        <button className="mt-4 lnl-btn-primary" disabled={busy} data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
                        {error && <div role="alert" className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="admin-login-error">{error}</div>}
                        <div className="mt-3 text-[11px] text-lnl-gray">Forgot your password? Ask a Super Administrator to reset it.</div>
                    </form>
                </div>
            </section>
        </div>
    );
}

// ============================================================================
// SIDEBAR NAVIGATION
// ============================================================================
const INQ_ROLES = ["super_admin", "website_admin", "admissions_officer", "department_rep", "analyst"];

const NAV_STRUCTURE = [
    { header: "MAIN", items: [
        { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: "any" },
    ]},
    { header: "WEBSITE", items: [
        { key: "homepage", label: "Homepage & SEO", icon: Home, roles: ["super_admin","website_admin"] },
        { key: "hero", label: "Hero Slides", icon: ImageIcon, roles: ["super_admin","website_admin"] },
        { key: "theme", label: "Color & Font Studio", icon: Sparkles, roles: ["super_admin","website_admin"] },
        { key: "programs", label: "Programs", icon: BookOpen, roles: ["super_admin","website_admin","content_editor"] },
        { key: "news", label: "News & Events", icon: Newspaper, roles: ["super_admin","website_admin","content_editor"] },
        { key: "statements", label: "Vision & Mission", icon: Sparkles, roles: ["super_admin","website_admin","content_editor"] },
        { key: "leadership", label: "Leadership", icon: Users2, roles: ["super_admin","website_admin","content_editor"] },
        { key: "facilities", label: "Facilities", icon: Building2, roles: ["super_admin","website_admin","content_editor"] },
        { key: "faqs", label: "FAQs", icon: HelpCircle, roles: ["super_admin","website_admin","content_editor"] },
        { key: "contact", label: "Contact & Socials", icon: Phone, roles: ["super_admin","website_admin"] },
        { key: "campus_submissions", label: "Campus Life Submissions", icon: MessageSquare, roles: ["super_admin","website_admin","content_editor"] },
        { key: "media", label: "Media Library", icon: ImageIcon, roles: ["super_admin","website_admin","content_editor"] },
    ]},
    { header: "ADMISSIONS", items: [
        { key: "inquiries", label: "Inquiries", icon: MessageSquare, roles: INQ_ROLES },
        { key: "applications", label: "Applications", icon: ClipboardList, roles: [...INQ_ROLES, "document_reviewer"] },
        { key: "tuition", label: "Tuition Leads", icon: Wallet, roles: INQ_ROLES },
        { key: "visits", label: "Campus Visits", icon: CalendarCheck, roles: INQ_ROLES },
        { key: "foreign", label: "Foreign Inquiries", icon: Globe2, roles: INQ_ROLES },
        { key: "requirements", label: "Requirements", icon: ListChecks, roles: ["super_admin","website_admin","content_editor"] },
        { key: "auto_responses", label: "Automated Responses", icon: Bell, roles: ["super_admin","website_admin","admissions_officer"] },
    ]},
    { header: "TEAM", items: [
        { key: "team_chat", label: "Team Chat", icon: MessageSquare, roles: "any" },
    ]},
    { header: "MANAGEMENT", items: [
        { key: "users", label: "Users & Roles", icon: Shield, roles: ["super_admin","website_admin"] },
        { key: "activity", label: "Activity Log", icon: History, roles: ["super_admin","website_admin"] },
        { key: "login_history", label: "Login History", icon: KeyRound, roles: ["super_admin","website_admin"] },
        { key: "export", label: "Export & Reports", icon: Database, roles: ["super_admin","website_admin","analyst"] },
    ]},
];

function canSeeNav(userRole, roles) {
    if (roles === "any") return true;
    if (!Array.isArray(roles)) return false;
    return roles.includes(userRole);
}

function Sidebar({ user, tab, setTab, collapsed, mobileOpen, closeMobile }) {
    return (
        <>
            {/* Mobile overlay */}
            {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={closeMobile} />}
            <aside
                data-testid="admin-sidebar"
                className={`fixed lg:static top-0 left-0 z-50 h-screen lg:h-auto lg:min-h-screen shrink-0 bg-[#0F1A11] text-white/85 flex flex-col overflow-hidden transition-[width] duration-200 ${mobileOpen ? "w-64" : "hidden"} lg:flex ${collapsed ? "lg:w-16" : "lg:w-64"}`}
            >
                <div className="px-4 py-4 border-b border-white/10 flex items-center gap-3">
                    <Logo size={34} />
                    {!collapsed && (
                        <div className="min-w-0">
                            <div className="font-heading font-bold text-white text-sm leading-tight truncate">Lyceum Northern Luzon</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-lnl-gold font-semibold">Est. 1994</div>
                        </div>
                    )}
                    <button onClick={closeMobile} className="lg:hidden ml-auto p-1 text-white/70" aria-label="Close menu"><X className="h-5 w-5" /></button>
                </div>
                <nav className="flex-1 overflow-y-auto py-2" aria-label="Admin navigation">
                    {NAV_STRUCTURE.map((section) => {
                        const items = section.items.filter((it) => canSeeNav(user.role, it.roles));
                        if (items.length === 0) return null;
                        return (
                            <div key={section.header} className="mt-4 first:mt-2">
                                {!collapsed && <div className="px-4 text-[10px] font-heading font-semibold uppercase tracking-[0.16em] text-white/40 mb-1.5">{section.header}</div>}
                                <div>
                                    {items.map((it) => {
                                        const active = tab === it.key;
                                        return (
                                            <button
                                                key={it.key}
                                                onClick={() => { setTab(it.key); closeMobile(); }}
                                                data-testid={`nav-${it.key}`}
                                                className={`w-full flex items-center gap-3 px-4 py-2 text-[13px] font-heading font-semibold transition-colors border-l-2 ${active ? "bg-lnl-green/20 text-white border-lnl-gold" : "text-white/75 hover:bg-white/5 hover:text-white border-transparent"}`}
                                                title={collapsed ? it.label : undefined}
                                            >
                                                <it.icon className="h-4 w-4 shrink-0" />
                                                {!collapsed && <span className="truncate text-left">{it.label}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </nav>
                <div className="border-t border-white/10 p-3">
                    <Link to="/" className={`flex items-center gap-2 text-xs text-white/70 hover:text-white ${collapsed ? "justify-center" : ""}`} data-testid="sidebar-view-site">
                        <ExternalLink className="h-3.5 w-3.5" /> {!collapsed && "View public website"}
                    </Link>
                </div>
            </aside>
        </>
    );
}

// ============================================================================
// TOP HEADER (in-shell)
// ============================================================================
function TopHeader({ user, onToggleSidebar, onOpenMobile, onLogout, onChangePassword, notifications }) {
    const [open, setOpen] = useState(false);
    return (
        <header className="sticky top-0 z-30 bg-white border-b border-lnl-border" data-testid="admin-topbar">
            <div className="flex items-center gap-3 px-4 lg:px-6 h-14">
                <button onClick={onOpenMobile} className="lg:hidden p-2 rounded-md hover:bg-lnl-off-white" aria-label="Open menu"><MenuIcon className="h-5 w-5" /></button>
                <button onClick={onToggleSidebar} className="hidden lg:inline-flex p-2 rounded-md hover:bg-lnl-off-white" aria-label="Toggle sidebar" data-testid="sidebar-toggle"><PanelLeft className="h-5 w-5" /></button>
                <div className="flex-1" />
                <div className="relative">
                    <button
                        className="relative p-2 rounded-md hover:bg-lnl-off-white"
                        aria-label={notifications > 0 ? `Notifications (${notifications} unread)` : "Notifications"}
                        data-testid="notifications-button"
                        title={notifications > 0 ? `${notifications} unread — inquiries + chat` : "No unread notifications"}
                    >
                        <Bell className="h-5 w-5 text-lnl-charcoal" />
                        {notifications > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-lnl-red text-white text-[10px] font-bold"
                                data-testid="notification-badge"
                            >
                                {notifications > 99 ? "99+" : notifications}
                            </span>
                        )}
                    </button>
                </div>
                <div className="relative">
                    <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md hover:bg-lnl-off-white" data-testid="profile-button" aria-expanded={open}>
                        <div className="w-8 h-8 rounded-full bg-lnl-green-bg text-lnl-green-deep grid place-items-center font-heading font-bold text-xs">{(user.name || user.email).split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}</div>
                        <div className="hidden sm:block text-left min-w-0">
                            <div className="text-[13px] font-heading font-semibold text-lnl-charcoal leading-tight truncate max-w-[160px]">{user.name}</div>
                            <div className="text-[10.5px] text-lnl-gray leading-tight truncate">{ROLE_LABEL[user.role] || user.role}</div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-lnl-gray" />
                    </button>
                    {open && (
                        <div className="absolute right-0 mt-1 w-56 bg-white border border-lnl-border rounded-md shadow-lg py-1 z-40" data-testid="profile-menu">
                            <div className="px-3 py-2 border-b border-lnl-border">
                                <div className="text-xs text-lnl-gray">Signed in as</div>
                                <div className="text-sm font-heading font-semibold text-lnl-charcoal truncate">{user.email}</div>
                            </div>
                            <button onClick={() => { setOpen(false); onChangePassword(); }} className="w-full text-left px-3 py-2 text-sm hover:bg-lnl-off-white flex items-center gap-2" data-testid="menu-change-password"><KeyRound className="h-4 w-4" /> Change password</button>
                            <button onClick={onLogout} className="w-full text-left px-3 py-2 text-sm hover:bg-lnl-off-white flex items-center gap-2 text-rose-700" data-testid="menu-signout"><LogOut className="h-4 w-4" /> Sign out</button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

// ============================================================================
// DASHBOARD HOME
// ============================================================================
function DashboardHome({ user, setTab }) {
    const [summary, setSummary] = useState(null);
    const [inquiries, setInquiries] = useState([]);
    const [applications, setApplications] = useState([]);
    const [activity, setActivity] = useState([]);
    const [status, setStatus] = useState({ pages: 30, programs: 0, news: 0, media: 0, lastUpdated: null });

    useEffect(() => {
        (async () => {
            try {
                const [s, i, a, ac, prog, ns, med, st] = await Promise.all([
                    adminApi.get("/admin/summary"),
                    adminApi.get("/admin/inquiries"),
                    adminApi.get("/admin/applications"),
                    adminApi.get("/cms/activity", { params: { limit: 6 } }).catch(() => ({ data: [] })),
                    adminApi.get("/programs"),
                    adminApi.get("/news", { params: { limit: 50 } }),
                    adminApi.get("/cms/media").catch(() => ({ data: [] })),
                    adminApi.get("/cms/settings").catch(() => ({ data: {} })),
                ]);
                setSummary(s.data); setInquiries(i.data.slice(0, 5)); setApplications(a.data.slice(0, 5));
                setActivity(ac.data.slice(0, 6));
                setStatus({ pages: 30, programs: prog.data.length, news: ns.data.length, media: med.data.length, lastUpdated: st.data.updated_at });
            } catch (e) { /* ignore */ }
        })();
    }, []);

    const greet = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";
    const kpiCards = summary ? [
        { key: "inquiries", label: "Inquiries", value: summary.inquiries_total, sub: `${summary.inquiries_new} new`, icon: MessageSquare, to: "inquiries" },
        { key: "applications", label: "Applications", value: summary.applications_total, sub: `${summary.applications_submitted} submitted`, icon: ClipboardList, to: "applications" },
        { key: "submitted", label: "Submitted", value: summary.applications_submitted, sub: "Awaiting review", icon: FileText, to: "applications" },
        { key: "accepted", label: "Accepted", value: summary.applications_accepted, sub: "Ready to enroll", icon: BadgeCheck, to: "applications" },
        { key: "enrolled", label: "Enrolled", value: summary.applications_enrolled, sub: "Confirmed slots", icon: GraduationCap, to: "applications" },
        { key: "visits", label: "Visits & Tuition", value: (summary.tuition_requests || 0) + (summary.campus_visits || 0), sub: `${summary.foreign_inquiries} foreign inquiries`, icon: TrendingUp, to: "tuition" },
    ] : [];

    const quickAccess = [
        { key: "homepage", title: "Edit Homepage", desc: "Update hero, content, sections and images.", icon: Home, roles: ["super_admin","website_admin"] },
        { key: "programs", title: "Programs", desc: "Manage academic programs.", icon: BookOpen, roles: ["super_admin","website_admin","content_editor"] },
        { key: "media", title: "Media Library", desc: "Upload and manage photos and documents.", icon: ImageIcon, roles: ["super_admin","website_admin","content_editor"] },
        { key: "news", title: "News & Events", desc: "Create and manage announcements.", icon: Newspaper, roles: ["super_admin","website_admin","content_editor"] },
        { key: "faqs", title: "FAQs", desc: "Manage frequently asked questions.", icon: HelpCircle, roles: ["super_admin","website_admin","content_editor"] },
        { key: "contact", title: "Contact & Socials", desc: "Update phone, email, address, social links.", icon: Phone, roles: ["super_admin","website_admin"] },
        { key: "statements", title: "Vision & Mission", desc: "Institutional statements and hymn.", icon: Sparkles, roles: ["super_admin","website_admin","content_editor"] },
        { key: "users", title: "Users & Roles", desc: "Manage staff accounts.", icon: Shield, roles: ["super_admin","website_admin"] },
    ].filter((q) => q.roles.includes(user.role));

    return (
        <div className="space-y-6" data-testid="admin-home">
            <div className="lnl-card p-6 bg-gradient-to-r from-lnl-green-bg to-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-2xl md:text-3xl font-bold text-lnl-charcoal">{greet}, {user.name} 👋</h1>
                        <p className="text-lnl-gray mt-1">Welcome back to your dashboard. Here's what's happening with your website today.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => setTab("homepage")} className="lnl-btn-primary" data-testid="edit-website-btn"><Pencil className="h-4 w-4" /> Edit Website</button>
                        <a href="/" target="_blank" rel="noopener noreferrer" className="lnl-btn-secondary" data-testid="view-website-btn"><ExternalLink className="h-4 w-4" /> View Website</a>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {kpiCards.map((c) => (
                    <button key={c.key} onClick={() => setTab(c.to)} className="text-left lnl-card p-4 hover:border-lnl-green/40 hover:shadow-sm transition-colors" data-testid={`kpi-${c.key}`}>
                        <div className="flex items-center gap-2"><c.icon className="h-4 w-4 text-lnl-green" /> <span className="text-xs text-lnl-gray font-heading font-semibold">{c.label}</span></div>
                        <div className="mt-2 font-heading text-3xl font-bold text-lnl-charcoal">{c.value}</div>
                        <div className="text-[11px] text-lnl-gray mt-0.5">{c.sub}</div>
                        <div className="mt-2 text-[11px] text-lnl-green-deep font-heading font-semibold inline-flex items-center gap-1">View <ArrowRight className="h-3 w-3" /></div>
                    </button>
                ))}
            </div>

            <div>
                <div className="flex items-center justify-between mb-3"><h2 className="font-heading text-lg font-bold text-lnl-charcoal">Quick access</h2></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {quickAccess.map((q) => (
                        <button key={q.key} onClick={() => setTab(q.key)} className="text-left lnl-card p-4 hover:border-lnl-green/40 hover:shadow-sm transition-colors" data-testid={`quick-${q.key}`}>
                            <div className="w-9 h-9 rounded-md bg-lnl-green-bg text-lnl-green-deep grid place-items-center"><q.icon className="h-4 w-4" /></div>
                            <div className="mt-3 font-heading font-bold text-lnl-charcoal">{q.title}</div>
                            <div className="text-xs text-lnl-gray mt-1">{q.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lnl-card p-5 lg:col-span-1">
                    <div className="flex items-center justify-between">
                        <div className="lnl-eyebrow">Recent inquiries</div>
                        <button onClick={() => setTab("inquiries")} className="text-xs text-lnl-green-deep font-heading font-semibold">See all</button>
                    </div>
                    <ul className="mt-3 space-y-3 text-sm">
                        {inquiries.map((r) => (
                            <li key={r.reference} className="pb-3 border-b border-lnl-border last:border-0 last:pb-0">
                                <div className="font-heading font-semibold text-lnl-charcoal">{r.full_name}</div>
                                <div className="text-xs text-lnl-gray">{r.applicant_type} · {r.main_concern}</div>
                                <div className="text-[11px] text-lnl-gray mt-0.5">{r.reference}</div>
                            </li>
                        ))}
                        {inquiries.length === 0 && <li className="text-lnl-gray text-xs">No inquiries yet.</li>}
                    </ul>
                </div>
                <div className="lnl-card p-5 lg:col-span-1">
                    <div className="flex items-center justify-between">
                        <div className="lnl-eyebrow">Recent applications</div>
                        <button onClick={() => setTab("applications")} className="text-xs text-lnl-green-deep font-heading font-semibold">See all</button>
                    </div>
                    <ul className="mt-3 space-y-3 text-sm">
                        {applications.map((a) => (
                            <li key={a.reference} className="pb-3 border-b border-lnl-border last:border-0 last:pb-0">
                                <div className="flex items-center justify-between">
                                    <div className="font-heading font-semibold text-lnl-charcoal">{a.steps?.personal?.full_name || a.contact_email}</div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_TONE[a.status] || ""}`}>{a.status}</span>
                                </div>
                                <div className="text-xs text-lnl-gray">{a.applicant_type} · {a.reference}</div>
                            </li>
                        ))}
                        {applications.length === 0 && <li className="text-lnl-gray text-xs">No applications yet.</li>}
                    </ul>
                </div>
                <div className="lnl-card p-5 lg:col-span-1">
                    <div className="flex items-center justify-between">
                        <div className="lnl-eyebrow">Website status</div>
                        <span className="text-[10px] px-2 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200 font-heading font-semibold">LIVE</span>
                    </div>
                    <ul className="mt-3 space-y-2 text-sm">
                        <li className="flex justify-between border-b border-lnl-border pb-2"><span className="text-lnl-gray">Public pages</span><span className="font-heading font-semibold">{status.pages}</span></li>
                        <li className="flex justify-between border-b border-lnl-border pb-2"><span className="text-lnl-gray">Programs</span><span className="font-heading font-semibold">{status.programs}</span></li>
                        <li className="flex justify-between border-b border-lnl-border pb-2"><span className="text-lnl-gray">Published news</span><span className="font-heading font-semibold">{status.news}</span></li>
                        <li className="flex justify-between border-b border-lnl-border pb-2"><span className="text-lnl-gray">Media files</span><span className="font-heading font-semibold">{status.media}</span></li>
                        <li className="flex justify-between"><span className="text-lnl-gray">Last content update</span><span className="font-heading font-semibold text-xs">{status.lastUpdated ? new Date(status.lastUpdated).toLocaleString() : "—"}</span></li>
                    </ul>
                    <a href="/" target="_blank" rel="noopener noreferrer" className="mt-3 lnl-btn-secondary w-full justify-center text-xs" data-testid="status-view-website"><ExternalLink className="h-4 w-4" /> View website</a>
                </div>
            </div>

            {activity.length > 0 && (
                <div className="lnl-card p-5">
                    <div className="flex items-center justify-between">
                        <div className="lnl-eyebrow">Recent activity</div>
                        <button onClick={() => setTab("activity")} className="text-xs text-lnl-green-deep font-heading font-semibold">Full log</button>
                    </div>
                    <ul className="mt-3 space-y-2 text-sm">
                        {activity.map((a) => (
                            <li key={a.id} className="flex items-start gap-3 pb-2 border-b border-lnl-border last:border-0">
                                <History className="h-3.5 w-3.5 text-lnl-gray mt-1" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-lnl-charcoal"><b>{a.actor_email}</b> — {a.action.replace(/\./g, " · ")} <span className="text-lnl-gray">{a.entity}/{a.entity_id}</span></div>
                                    <div className="text-[11px] text-lnl-gray">{new Date(a.at).toLocaleString()}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// USERS TAB (unchanged behavior, reorganized under section)
// ============================================================================
function Dialog({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4">
            <div className="w-full max-w-md bg-white rounded-md border border-lnl-border shadow-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-lnl-border"><div className="font-heading font-bold text-lnl-charcoal">{title}</div><button onClick={onClose} className="p-1 rounded hover:bg-lnl-off-white" aria-label="Close"><X className="h-4 w-4" /></button></div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

function UsersSection({ currentUser }) {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [passwordFor, setPasswordFor] = useState(null);
    const [permsFor, setPermsFor] = useState(null);

    async function load() {
        try { const [u, r] = await Promise.all([adminApi.get("/auth/users"), adminApi.get("/auth/roles")]); setUsers(u.data); setRoles(r.data); }
        catch (e) { toast.error(formatErr(e)); }
    }
    useEffect(() => { load(); }, []);

    async function toggleActive(u) { try { await adminApi.patch(`/auth/users/${u.id}`, { is_active: !u.is_active }); load(); toast.success(u.is_active ? "Deactivated" : "Activated"); } catch (e) { toast.error(formatErr(e)); } }
    async function deleteUser(u) { if (!window.confirm(`Delete ${u.email}?`)) return; try { await adminApi.delete(`/auth/users/${u.id}`); load(); toast.success("User removed"); } catch (e) { toast.error(formatErr(e)); } }

    const canEditPerms = currentUser.role === "super_admin";

    return (
        <div data-testid="users-tab">
            <div className="flex items-center justify-between mb-4">
                <div><div className="lnl-eyebrow">Access control</div><h2 className="font-heading text-xl font-bold text-lnl-charcoal">Staff users & roles</h2></div>
                <button onClick={() => setShowCreate(true)} className="lnl-btn-primary" data-testid="user-create-btn"><UserPlus className="h-4 w-4" /> New user</button>
            </div>
            <div className="lnl-card overflow-hidden">
                <table className="w-full text-sm border-collapse" data-testid="users-table">
                    <thead className="bg-lnl-off-white text-left text-xs uppercase tracking-widest text-lnl-gray"><tr><th className="p-3">Name / Email</th><th className="p-3">Role</th><th className="p-3">Dept</th><th className="p-3">Status</th><th className="p-3">Last sign-in</th><th className="p-3">Actions</th></tr></thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t border-lnl-border align-top">
                                <td className="p-3"><div className="font-heading font-semibold text-lnl-charcoal">{u.name}</div><div className="text-xs text-lnl-gray">{u.email}</div></td>
                                <td className="p-3"><span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-lnl-green-deep bg-lnl-green-bg border border-lnl-green/20 rounded px-2 py-0.5">{u.role_label || u.role}</span></td>
                                <td className="p-3 text-lnl-gray">{u.department || "—"}</td>
                                <td className="p-3"><span className={`text-[10px] px-2 py-1 rounded border ${u.is_active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-neutral-100 text-neutral-700 border-neutral-200"}`}>{u.is_active ? "Active" : "Disabled"}</span></td>
                                <td className="p-3 text-xs text-lnl-gray">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                                <td className="p-3 flex flex-wrap gap-1.5">
                                    <button onClick={() => setEditing(u)} className="text-xs font-heading font-semibold text-lnl-green-deep hover:underline" data-testid={`user-edit-${u.email}`}>Edit</button>
                                    <button onClick={() => setPasswordFor(u)} className="text-xs font-heading font-semibold text-lnl-charcoal hover:underline">Set password</button>
                                    {canEditPerms && u.role !== "super_admin" && (
                                        <button onClick={() => setPermsFor(u)} className="text-xs font-heading font-semibold text-indigo-700 hover:underline" data-testid={`user-perms-${u.email}`}>Permissions</button>
                                    )}
                                    <button onClick={() => toggleActive(u)} className="text-xs font-heading font-semibold text-lnl-charcoal hover:underline">{u.is_active ? "Disable" : "Enable"}</button>
                                    {currentUser.role === "super_admin" && u.email !== currentUser.email && <button onClick={() => deleteUser(u)} className="text-xs font-heading font-semibold text-rose-700 hover:underline inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>}
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-lnl-gray">No users yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            {showCreate && <UserForm roles={roles} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
            {editing && <UserForm roles={roles} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {passwordFor && <SetPasswordDialog user={passwordFor} onClose={() => setPasswordFor(null)} onSaved={() => setPasswordFor(null)} />}
            {permsFor && <PermissionsDialog user={permsFor} onClose={() => setPermsFor(null)} onSaved={() => { setPermsFor(null); load(); }} />}
        </div>
    );
}

function PermissionsDialog({ user, onClose, onSaved }) {
    const [catalog, setCatalog] = useState(null);
    const [selected, setSelected] = useState(new Set(user.permissions || []));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [{ data }, { data: userFull }] = await Promise.all([
                    adminApi.get("/auth/permissions"),
                    adminApi.get(`/auth/users`),
                ]);
                setCatalog(data);
                const found = (userFull || []).find((x) => x.id === user.id);
                if (found) setSelected(new Set(found.permissions || []));
            } catch (e) { toast.error(formatErr(e)); }
        })();
    }, [user.id]);

    function toggle(key) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    async function save() {
        setBusy(true);
        try {
            await adminApi.patch(`/auth/users/${user.id}/permissions`, { permissions: Array.from(selected) });
            toast.success("Permissions updated");
            onSaved();
        } catch (e) { toast.error(formatErr(e)); }
        finally { setBusy(false); }
    }

    const roleDefaults = new Set(catalog?.role_defaults?.[user.role] || []);

    return (
        <Dialog title={`Permissions — ${user.name}`} onClose={onClose}>
            <div className="text-xs text-lnl-gray mb-3">
                Role: <b className="text-lnl-charcoal">{user.role_label || user.role}</b>. Boxes shown with a green dot are already granted by the role.
                Ticking additional boxes gives this person extra permissions on top of their role.
            </div>
            {!catalog && <div className="text-lnl-gray text-sm">Loading permissions…</div>}
            {catalog && (
                <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
                    {catalog.groups.map((g) => (
                        <div key={g.key}>
                            <div className="text-[11px] uppercase tracking-widest font-heading font-semibold text-lnl-green-deep mb-1.5">{g.key}</div>
                            <div className="space-y-1.5">
                                {g.items.map((it) => {
                                    const byRole = roleDefaults.has(it.key);
                                    const checked = selected.has(it.key) || byRole;
                                    return (
                                        <label key={it.key} className={`flex items-center gap-2 text-sm ${byRole ? "text-lnl-gray" : "text-lnl-charcoal"}`} data-testid={`perm-${it.key}`}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={byRole}
                                                onChange={() => !byRole && toggle(it.key)}
                                            />
                                            {byRole && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Granted by role" />}
                                            <span>{it.label}</span>
                                            <span className="ml-auto text-[10px] text-lnl-gray">{it.key}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex justify-end gap-2 pt-4 mt-3 border-t border-lnl-border">
                <button onClick={onClose} className="lnl-btn-secondary text-sm py-2">Cancel</button>
                <button onClick={save} disabled={busy || !catalog} className="lnl-btn-primary text-sm py-2" data-testid="perm-save">
                    <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save permissions"}
                </button>
            </div>
        </Dialog>
    );
}

function UserForm({ initial, roles, onClose, onSaved }) {
    const isEdit = !!initial;
    const defaultPrefs = {
        receive_department_emails: true,
        receive_new_submissions: true,
        receive_status_changes: false,
        receive_assignment_emails: true,
        receive_urgent: true,
    };
    const initialPrefs = { ...defaultPrefs, ...(initial?.notification_prefs || {}) };
    const [f, setF] = useState(initial
        ? { email: initial.email, name: initial.name, role: initial.role, department: initial.department || "", notification_prefs: initialPrefs }
        : { email: "", name: "", role: "admissions_officer", department: "", password: "", notification_prefs: defaultPrefs });
    const [busy, setBusy] = useState(false);
    function togglePref(key) {
        setF((prev) => ({
            ...prev,
            notification_prefs: { ...prev.notification_prefs, [key]: !prev.notification_prefs[key] },
        }));
    }
    async function submit(e) {
        e.preventDefault(); setBusy(true);
        try {
            if (isEdit) {
                await adminApi.patch(`/auth/users/${initial.id}`, {
                    name: f.name, role: f.role, department: f.department,
                    notification_prefs: f.notification_prefs,
                });
            } else {
                await adminApi.post("/auth/users", { email: f.email, name: f.name, role: f.role, department: f.department, password: f.password });
                // notification_prefs are set on next edit; create endpoint doesn't accept them
            }
            toast.success(isEdit ? "Saved" : "User created"); onSaved();
        } catch (err) { toast.error(formatErr(err)); }
        finally { setBusy(false); }
    }
    const prefLabels = [
        ["receive_department_emails", "Receive department emails (master switch)"],
        ["receive_new_submissions", "New submission notifications"],
        ["receive_status_changes", "Status change notifications"],
        ["receive_assignment_emails", "Assignment notifications"],
        ["receive_urgent", "Urgent / important notifications"],
    ];
    return (
        <Dialog title={isEdit ? `Edit ${initial.email}` : "Create staff user"} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="user-create-form">
                <div><label className="text-xs text-lnl-gray font-heading font-semibold">Full name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" data-testid="user-name" /></div>
                {!isEdit && <div><label className="text-xs text-lnl-gray font-heading font-semibold">Email</label><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" data-testid="user-email" /></div>}
                <div><label className="text-xs text-lnl-gray font-heading font-semibold">Role</label>
                    <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white" data-testid="user-role">
                        {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                </div>
                <div><label className="text-xs text-lnl-gray font-heading font-semibold">Department (optional)</label><input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" data-testid="user-department" /></div>
                {!isEdit && <div><label className="text-xs text-lnl-gray font-heading font-semibold">Temporary password (min 8 chars)</label><PasswordInput required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" testid="user-password" /></div>}

                {isEdit && (
                    <div className="rounded-md border border-lnl-border p-3 bg-lnl-off-white" data-testid="notification-prefs-section">
                        <div className="text-xs font-heading font-semibold text-lnl-charcoal">Email notification preferences</div>
                        <p className="text-[11px] text-lnl-gray mt-0.5 mb-2">This staff member will receive department emails only when the master switch is on AND the specific event type is enabled.</p>
                        <div className="space-y-1.5">
                            {prefLabels.map(([key, label]) => (
                                <label key={key} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`pref-${key}`}>
                                    <input
                                        type="checkbox"
                                        checked={!!f.notification_prefs[key]}
                                        onChange={() => togglePref(key)}
                                        disabled={key !== "receive_department_emails" && !f.notification_prefs.receive_department_emails}
                                    />
                                    <span className={key !== "receive_department_emails" && !f.notification_prefs.receive_department_emails ? "text-lnl-gray" : "text-lnl-charcoal"}>
                                        {label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="lnl-btn-secondary">Cancel</button><button disabled={busy} className="lnl-btn-primary" data-testid="user-create-submit">{busy ? "Saving…" : (isEdit ? "Save" : "Create user")}</button></div>
            </form>
        </Dialog>
    );
}

function SetPasswordDialog({ user, onClose, onSaved }) {
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit(e) {
        e.preventDefault(); setBusy(true);
        try { await adminApi.patch(`/auth/users/${user.id}`, { password }); toast.success("Password updated"); onSaved(); }
        catch (err) { toast.error(formatErr(err)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog title={`Set password — ${user.email}`} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <div><label className="text-xs text-lnl-gray font-heading font-semibold">New password (min 8 chars)</label><PasswordInput required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" testid="set-password-new" /></div>
                <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="lnl-btn-secondary">Cancel</button><button disabled={busy} className="lnl-btn-primary">{busy ? "Saving…" : "Update password"}</button></div>
            </form>
        </Dialog>
    );
}

function ChangeMyPasswordDialog({ onClose }) {
    const { changePassword } = useAuth();
    const [cur, setCur] = useState("");
    const [nw, setNw] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit(e) {
        e.preventDefault(); setBusy(true);
        try { await changePassword(cur, nw); toast.success("Password changed"); onClose(); }
        catch (err) { toast.error(formatErr(err)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog title="Change my password" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <div><label className="text-xs text-lnl-gray font-heading font-semibold">Current password</label><PasswordInput required value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" testid="my-pwd-current" /></div>
                <div><label className="text-xs text-lnl-gray font-heading font-semibold">New password (min 8)</label><PasswordInput required minLength={8} value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password" testid="my-pwd-new" /></div>
                <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="lnl-btn-secondary">Cancel</button><button disabled={busy} className="lnl-btn-primary" data-testid="my-pwd-submit">{busy ? "Saving…" : "Change password"}</button></div>
            </form>
        </Dialog>
    );
}

// ============================================================================
// ADMISSIONS TABLES (reused)
// ============================================================================
function AdmissionsTable({ endpoint, testid, columns, statusColumn, statuses, patchPath, isReadOnly, onOpen, notifyOption }) {
    const [notify, setNotify] = useState(true);
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    async function load() { try { const { data } = await adminApi.get(endpoint); setRows(data); } catch (e) { toast.error(formatErr(e)); } }
    useEffect(() => { load(); }, [endpoint]);
    async function updateStatus(ref, status) {
        const body = notifyOption ? { status, notify_applicant: notify } : { status };
        try { await adminApi.patch(`${patchPath}/${ref}`, body); toast.success(notifyOption && notify ? `Updated ${ref} — applicant will be emailed if a template is set for “${status}”` : `Updated ${ref}`); load(); } catch (e) { toast.error(formatErr(e)); }
    }
    const filtered = q ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) : rows;
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-lnl-gray">{filtered.length} record{filtered.length === 1 ? "" : "s"}</div>
                {notifyOption && (
                    <label className="flex items-center gap-2 text-xs font-heading font-semibold text-lnl-charcoal mx-3">
                        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="notify-applicant-checkbox" />
                        Notify applicant by email on status change
                    </label>
                )}
                <div className="relative w-full max-w-sm">
                    <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-lnl-gray" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-3 py-2 rounded-md border border-lnl-border text-sm" />
                </div>
            </div>
            <div className="lnl-card overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead className="bg-lnl-off-white text-left text-xs uppercase tracking-widest text-lnl-gray"><tr>{columns.map((c) => <th key={c.key} className="p-3">{c.label}</th>)}{statusColumn && <th className="p-3">Status</th>}{onOpen && <th className="p-3">Auto-reply</th>}</tr></thead>
                    <tbody>
                        {filtered.map((r) => (
                            <tr key={r.reference} className="border-t border-lnl-border">
                                {columns.map((c) => <td key={c.key} className="p-3">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                                {statusColumn && (
                                    <td className="p-3">
                                        {patchPath ? (
                                            <select disabled={isReadOnly} defaultValue={r.status} onChange={(e) => updateStatus(r.reference, e.target.value)} className="border border-lnl-border rounded px-2 py-1 text-xs bg-white disabled:opacity-50">
                                                {statuses.map((s) => <option key={s}>{s}</option>)}
                                            </select>
                                        ) : (<span className="text-xs">{r.status}</span>)}
                                    </td>
                                )}
                                {onOpen && (
                                    <td className="p-3 whitespace-nowrap">
                                        <button onClick={() => onOpen(r.reference)} className="text-xs font-heading font-semibold text-lnl-green hover:underline" data-testid={`open-record-${r.reference}`}>
                                            {r.auto_response?.status || "—"} · Open
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {filtered.length === 0 && <tr><td colSpan={columns.length + (statusColumn ? 1 : 0) + (onOpen ? 1 : 0)} className="p-8 text-center text-lnl-gray">No records.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================================
// MAIN SHELL
// ============================================================================
export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("dashboard");
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [inquiriesNew, setInquiriesNew] = useState(0);
    const [chatUnread, setChatUnread] = useState(0);
    const [drawer, setDrawer] = useState(null);
    const myPerms = new Set(user?.effective_permissions || []);
    const canOpenRecords = user?.role === "super_admin" || ["inquiries.respond", "inquiries.view", "applications.view", "autoresp.view"].some((p) => myPerms.has(p));
    const opener = (kind) => (canOpenRecords ? (reference) => setDrawer({ kind, reference }) : undefined);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        async function poll() {
            try {
                const [s, r] = await Promise.all([
                    adminApi.get("/admin/summary").catch(() => ({ data: {} })),
                    adminApi.get("/chat/rooms").catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setInquiriesNew(s.data?.inquiries_new || 0);
                setChatUnread((r.data || []).reduce((sum, room) => sum + (room.unread || 0), 0));
            } catch { /* ignore */ }
        }
        poll();
        const id = setInterval(poll, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user]);

    // When the user is actively on Team Chat, the panel handles read-marking itself,
    // so drop the chat count from the bell to avoid a stale badge.
    const notifications = inquiriesNew + (tab === "team_chat" ? 0 : chatUnread);

    if (user === undefined) return <div className="lnl-container py-16 text-lnl-gray">Loading…</div>;
    if (user === null) return <LoginCard />;

    const isReadOnly = user.role === "analyst";
    const canManageUsers = user && CAN_MANAGE_USERS.has(user.role);
    const canEditContent = ["super_admin","website_admin","content_editor"].includes(user.role);
    const canManageSettings = ["super_admin","website_admin"].includes(user.role);

    const sectionTitle = {
        dashboard: "Dashboard", homepage: "Homepage & SEO", hero: "Hero Slides", theme: "Color & Font Studio", programs: "Academic Programs",
        news: "News & Events", statements: "Vision, Mission, Hymn", leadership: "Leadership",
        facilities: "Facilities", faqs: "FAQs", contact: "Contact & Socials", media: "Media Library",
        campus_submissions: "Campus Life Submissions",
        team_chat: "Team Chat", auto_responses: "Automated Responses",
        inquiries: "Inquiries", applications: "Applications", tuition: "Tuition Leads",
        visits: "Campus Visits", foreign: "Foreign Inquiries", requirements: "Admission Requirements",
        users: "Users & Roles", activity: "Activity Log", login_history: "Login History", export: "Export & Reports",
    };
    const sectionSubtitle = {
        homepage: "Institution details, hero content, socials and SEO.",
        theme: "Presets, fonts, colours and live preview — save to publish site-wide.",
        contact: "Address, phone, email, CONTACT US button and social icons — controlled from one place.",
        campus_submissions: "Review, approve, edit and moderate visitor-submitted Campus Life photos & stories.",
        media: "Photos, banners, documents and downloads for the website.",
        auto_responses: "Create the emails inquirers receive automatically — no coding needed.",
        activity: "Full history of who changed what and when.",
        login_history: "Sign-in and sign-out audit trail (successful, failed, blocked, logout).",
        users: "Add, edit and disable staff accounts and their permissions.",
    };

    return (
        <div className="min-h-screen bg-lnl-off-white flex" data-testid="admin-shell">
            <Sidebar user={user} tab={tab} setTab={setTab} collapsed={collapsed} mobileOpen={mobileOpen} closeMobile={() => setMobileOpen(false)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <TopHeader
                    user={user}
                    onToggleSidebar={() => setCollapsed((c) => !c)}
                    onOpenMobile={() => setMobileOpen(true)}
                    onLogout={logout}
                    onChangePassword={() => setShowPwd(true)}
                    notifications={notifications}
                />
                <main className="flex-1 p-4 lg:p-8" data-testid="admin-main">
                    {tab !== "dashboard" && (
                        <div className="mb-6">
                            <div className="text-[11px] uppercase tracking-widest text-lnl-gray font-heading font-semibold">Admin</div>
                            <h1 className="font-heading text-2xl md:text-3xl font-bold text-lnl-charcoal mt-1">{sectionTitle[tab] || tab}</h1>
                            {sectionSubtitle[tab] && <p className="text-lnl-gray mt-1">{sectionSubtitle[tab]}</p>}
                        </div>
                    )}

                    {tab === "dashboard" && <DashboardHome user={user} setTab={setTab} />}

                    {tab === "homepage" && canManageSettings && <SettingsPanel />}
                    {tab === "hero" && canManageSettings && <HeroSlidesPanel />}
                    {tab === "theme" && canManageSettings && <ThemeStudio />}
                    {tab === "contact" && canManageSettings && <SettingsPanel />}
                    {tab === "campus_submissions" && canEditContent && <CampusSubmissionsPanel />}
                    {tab === "team_chat" && <TeamChatPanel />}
                    {tab === "auto_responses" && <AutoResponsesPanel />}
                    {tab === "media" && canEditContent && <MediaPanel />}
                    {tab === "news" && canEditContent && <NewsPanel />}
                    {tab === "faqs" && canEditContent && <FaqsPanel />}
                    {tab === "facilities" && canEditContent && <FacilitiesPanel />}
                    {tab === "leadership" && canEditContent && <LeadershipPanel />}
                    {tab === "statements" && canEditContent && <StatementsPanel />}
                    {tab === "programs" && canEditContent && <ProgramsPanel />}
                    {tab === "requirements" && canEditContent && <RequirementsPanel />}
                    {tab === "export" && (canManageSettings || user.role === "analyst") && <ExportPanel />}
                    {tab === "activity" && canManageSettings && <ActivityPanel />}
                    {tab === "login_history" && canManageSettings && <LoginActivityPanel />}
                    {tab === "users" && canManageUsers && <UsersSection currentUser={user} />}

                    {tab === "inquiries" && (
                        <AdmissionsTable
                            endpoint="/admin/inquiries" testid="admin-inquiries-table" patchPath="/admin/inquiries"
                            statusColumn statuses={["New","Contact Attempted","Contacted","Qualified","Application Started","Documents Pending","Submitted","For Review","Accepted","Enrolled","Closed"]}
                            isReadOnly={isReadOnly} onOpen={opener("inquiry")}
                            columns={[
                                { key: "reference", label: "Reference", render: (r) => <span className="font-heading font-semibold">{r.reference}</span> },
                                { key: "name", label: "Name / Email", render: (r) => <div><div className="font-heading font-semibold">{r.full_name}</div><div className="text-xs text-lnl-gray">{r.email} · {r.mobile}</div></div> },
                                { key: "inquiry_type", label: "Inquiry Type", render: (r) => r.inquiry_type || r.main_concern || "—" },
                                { key: "applicant_type", label: "Applicant" },
                                { key: "priority", label: "Priority", render: (r) => r.priority || "Normal" },
                                { key: "created_at", label: "Date", render: (r) => <span className="text-xs text-lnl-gray">{new Date(r.created_at).toLocaleDateString()}</span> },
                            ]}
                        />
                    )}
                    {tab === "applications" && (
                        <AdmissionsTable
                            endpoint="/admin/applications" testid="admin-applications-table" patchPath="/admin/applications"
                            statusColumn statuses={APP_STATUSES} isReadOnly={isReadOnly} onOpen={opener("application")} notifyOption
                            columns={[
                                { key: "reference", label: "Reference", render: (r) => <span className="font-heading font-semibold">{r.reference}</span> },
                                { key: "applicant", label: "Applicant", render: (r) => <div><div className="font-heading font-semibold">{r.steps?.personal?.full_name || r.contact_email}</div><div className="text-xs text-lnl-gray">{r.contact_email}</div></div> },
                                { key: "applicant_type", label: "Type" },
                                { key: "program", label: "Program", render: (r) => r.steps?.program?.slug || "—" },
                                { key: "documents", label: "Docs", render: (r) => (r.documents || []).length },
                                { key: "created_at", label: "Started", render: (r) => <span className="text-xs text-lnl-gray">{new Date(r.created_at).toLocaleDateString()}</span> },
                            ]}
                        />
                    )}
                    {tab === "tuition" && (
                        <AdmissionsTable
                            endpoint="/admin/tuition-leads" testid="admin-tuition-table" onOpen={opener("tuition_lead")}
                            columns={[
                                { key: "reference", label: "Reference" },
                                { key: "name", label: "Requester", render: (r) => <div><div className="font-heading font-semibold">{r.full_name}</div><div className="text-xs text-lnl-gray">{r.email} · {r.mobile}</div></div> },
                                { key: "request_type", label: "Request" },
                                { key: "preferred_program", label: "Program" },
                                { key: "created_at", label: "Date", render: (r) => <span className="text-xs text-lnl-gray">{new Date(r.created_at).toLocaleDateString()}</span> },
                            ]}
                        />
                    )}
                    {tab === "visits" && (
                        <AdmissionsTable
                            endpoint="/admin/campus-visits" testid="admin-visits-table" onOpen={opener("campus_visit")}
                            statusColumn statuses={["Requested","Confirmed","Rescheduled","Completed","Cancelled"]}
                            columns={[
                                { key: "reference", label: "Reference" },
                                { key: "visitor_name", label: "Visitor" },
                                { key: "preferred_date", label: "Date" },
                                { key: "preferred_program", label: "Program" },
                            ]}
                        />
                    )}
                    {tab === "foreign" && (
                        <AdmissionsTable
                            endpoint="/admin/foreign-inquiries" testid="admin-foreign-table" onOpen={opener("foreign_inquiry")}
                            columns={[
                                { key: "reference", label: "Reference" },
                                { key: "full_name", label: "Name" },
                                { key: "nationality", label: "Nationality" },
                                { key: "preferred_program", label: "Program" },
                                { key: "created_at", label: "Date", render: (r) => <span className="text-xs text-lnl-gray">{new Date(r.created_at).toLocaleDateString()}</span> },
                            ]}
                        />
                    )}
                </main>
            </div>
            {showPwd && <ChangeMyPasswordDialog onClose={() => setShowPwd(false)} />}
            {drawer && <RecordDrawer kind={drawer.kind} reference={drawer.reference} user={user} onClose={() => setDrawer(null)} />}
        </div>
    );
}
